/**
 * ContentPlanRoutePage — route-level wrapper around ContentPlansPage.
 *
 * Mounted at `/project/:projectId/content-plan`. Access is controlled by
 * content-plan/config/access. Reads the
 * projectId from the URL, the current user from AuthContext, and constructs
 * the StrategyService internally. The route is registered unconditionally in
 * App.jsx so cold-load deep links do not fall through before admin state is
 * loaded; this wrapper performs the admin gate once AuthContext settles.
 *
 * The inner <ContentPlansPage> still takes the same props it always has,
 * so all internal sub-tab routing (?cpTab=…) continues to work unchanged.
 */

import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { ContentPlansPage } from './ContentPlansPage';
import { canAccessContentPlan, isContentPlanAdminOnly } from '@modules/content-plan/config/access';
import { useProjectCapability } from '@modules/billing-access';

export const ContentPlanRoutePage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentUser, isAdmin, adminLoaded } = useAuth() as {
    currentUser: { uid: string } | null;
    isAdmin: boolean;
    adminLoaded: boolean;
  };

  const strategyService = useMemo(() => {
    if (!projectId) return null;
    return new StrategyService(projectId);
  }, [projectId]);
  const contentPlanAccess = useProjectCapability(projectId, 'content_plan.access');

  if (!projectId) {
    return <Navigate to="/all-projects" replace />;
  }

  // Match TopicDetailRoutePage: on cold-load `currentUser` can resolve before
  // the Firestore admin listener sets `isAdmin`. Redirect only after the first
  // admin snapshot has loaded, otherwise direct URLs bounce to /all-projects.
  if (!currentUser || (isContentPlanAdminOnly() && (!adminLoaded || contentPlanAccess.loading))) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading…</Typography>
      </Box>
    );
  }

  if (!canAccessContentPlan({ isAdmin, hasBillingAccess: contentPlanAccess.allowed })) {
    return <Navigate to="/all-projects" replace />;
  }

  if (!strategyService) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to initialize strategy service.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: '24px' }}>
      <ContentPlansPage
        projectId={projectId}
        userId={currentUser.uid}
        strategyService={strategyService}
      />
    </Box>
  );
};

export default ContentPlanRoutePage;
